package gr.tech.GRAttestation.pages.admin.stores

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.BoxShadow
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.NavBreadcrumb
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.ReceiptGeneratorData
import gr.tech.GRAttestation.infrastructure.api.adminRecordGenReceiptActivity
import gr.tech.GRAttestation.models.attestation.AttestationDTO
import gr.tech.GRAttestation.models.pdf.ItemTracker
import gr.tech.GRAttestation.models.stores.StoreDTO
import gr.tech.GRAttestation.navigation.AdminSitePage
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.util.GeneratePDFTool
import gr.tech.GRAttestation.util.PDFGenStatus
import gr.tech.GRAttestation.util.formatCurrency
import gr.tech.GRAttestation.util.todayString
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

data class ReceiptItem(
    val quantity: Int,
    val skuModel: String,
    val itemDescription: String,
    val unitPrice: Long,
)

@Page("{id}/receipts")
@Composable
fun AdminStoreReceiptPage() {
    AdminPageLayout() { context ->
        val storeId = context.route.params["id"]

        if (storeId == null) {
            context.router.navigateTo(AdminSitePage.Projects.route)
            return@AdminPageLayout
        }

        ReceiptGeneratorData.attestation?.let { attestation ->
            ReceiptGeneratorData.store?.let { store ->
                AdminStoreReceiptScreen(
                    storeData = store,
                    attestationData = attestation,
                    context = context
                )
            }
        } ?: run {
            context.router.navigateTo(AdminSitePage.Stores.passStoreId(storeId))
        }
    }
}

@Composable
fun AdminStoreReceiptScreen(
    storeData: StoreDTO,
    attestationData: AttestationDTO,
    context: PageContext
) {
    val generator = GeneratePDFTool()
    var generationStatus: PDFGenStatus by remember { mutableStateOf(PDFGenStatus.GENERATING) }
    var triggerGeneration by remember { mutableStateOf(false) }

    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    LaunchedEffect(triggerGeneration) {
        adminRecordGenReceiptActivity(
            formId = attestationData.id,
            storeId = attestationData.storeId,
            onResponse = {},
            onError = {}
        )

        generator.genPDF(
            listOfIds = listOf("receipts-page"),
            receipt = true,
            onGenerationStatus = { status ->
                generator.getBlob(
                    onBlobReady = { blob ->
                        console.log("Blob ready")
                        generationStatus = PDFGenStatus.READY_DOWNLOAD
                        alertManager.addAlert(UserAlert(message = "PDF ready to download!", status = 2))
                    },
                    onError = {
                        console.error("Blob generation failed!")
                        generationStatus = PDFGenStatus.FAILED
                        alertManager.addAlert(UserAlert(message = "Error generating PDF!", status = 0))
                        document.getElementsByClassName("html2pdf__overlay").item(0)?.remove()
                        document.getElementsByClassName("html2canvas-container").item(0)?.remove()
                    }
                )
            },
        )
    }

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Colors.LightGray)
            .fillMaxSize(),
    ) {
        NavBreadcrumb(ctx = context)

        // Top Row Buttons
        Row(
            modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
            horizontalArrangement = Arrangement.Start,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                attrs = Modifier
                    .classNames("btn", "btn-primary")
                    .disabled(generationStatus != PDFGenStatus.READY_DOWNLOAD)
                    .onClick {
                        generator.downloadPDFtoBrowser(
                            onError = { console.error("Error Downloading PDF to browser.") }
                        )
                    }
                    .margin(right = 16.px)
                    .toAttrs()
            ) {
                FaDownload(modifier = Modifier.margin(right = 8.px))
                Text("Download")
            }

            when (generationStatus) {
                PDFGenStatus.GENERATING -> {
                    loadingSpinner(modifier = Modifier.margin(left = 4.px))
                }
                PDFGenStatus.INSTANCE_READY -> {}
                PDFGenStatus.BLOB_READY -> {}
                PDFGenStatus.READY_DOWNLOAD -> {}
                PDFGenStatus.FAILED -> {
                    FaXmark(modifier = Modifier.margin(left = 4.px).color(Colors.Red))
                }
            }
        }


        // Receipts Form
        Div(Modifier.backgroundColor(Colors.White).margin(leftRight = autoLength).toAttrs {
            id("receipts-page")
        }) {

            val trackers = attestationData.salesData.map {
                ItemTracker(it, it.quantity)
            }

            val receiptBoxes = mutableListOf<List<ReceiptItem>>()

            while (trackers.any { it.remaining > 0 }) {
                val boxItems = mutableListOf<ReceiptItem>()

                for (tracker in trackers) {
                    if (tracker.remaining <= 0) continue

                    val salesData = tracker.salesData
                    val takeCount = minOf(tracker.remaining, salesData.skuModel.amountPerReceipt)

                    //repeat(takeCount) {
                    boxItems.add(
                        ReceiptItem(
                            quantity = takeCount,
                            skuModel = salesData.skuModel.sku,
                            itemDescription = salesData.skuModel.description,
                            unitPrice = salesData.skuModel.unitPrice
                        )
                    )

                    tracker.remaining -= takeCount
                }

                receiptBoxes.add(boxItems)
            }
            val pages = receiptBoxes.chunked(9)

            // Render each page for this salesData
            var receiptCounter = 1
            pages.forEachIndexed { index, pageBoxes ->
                Div(receiptPageStyle.toModifier().toAttrs()) {
                    Div(ReceiptsContainerStyle.toAttrs()) {
                        pageBoxes.forEachIndexed { _, receiptItems ->
                            ReceiptBox(
                                store = storeData,
                                attestationId = attestationData.id,
                                date = todayString,
                                receiptNumb = receiptCounter++,
                                receiptItems = receiptItems
                            )
                        }
                    }
                }
            }

            // Start launchedEffect to trigger generation
            triggerGeneration = true

        }
    }
}


@Composable
fun ReceiptBox(
    store: StoreDTO,
    attestationId: Int,
    date: String = todayString,
    receiptNumb: Int,
    receiptItems: List<ReceiptItem>
) {
    var totalPrice: Long by remember { mutableStateOf(0) }

    Column(
        modifier = Modifier
            .width(300.px)
            .height(350.px)
            .borderRadius(4.px)
            .padding(3.px)
            .border(1.px, LineStyle.Solid, Colors.Black)
            .boxShadow(
                BoxShadow.of(
                offsetX = 0.px,
                offsetY = 10.px,
                blurRadius = 10.px,
                spreadRadius = 1.px,
                color = Color.rgba(0,0,0,0.1.toFloat())
            ))
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            SpanText(
                text = store.storeName,
                modifier = Modifier.defaultText()
            )

            P(attrs = GenDefaultText
                .fontSize(1.2.cssRem).fontWeight(FontWeight.Bold).toAttrs()
            ) {
                Text("RECEIPT")
            }
        }

        P(attrs = ReceiptTextStyle.toAttrs()) {
            SpanText("${store.storeAddress} ${store.storeCity}, ${store.storeState} ${store.storeZip}", Modifier.fontWeight(FontWeight.Light))
        }

        HorizontalDivider(GenHorizontalDivider.toModifier().margin(topBottom = 8.px))

        Row(
            modifier = Modifier.fillMaxWidth().gap(8.px).margin(bottom = 6.px),
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                SpanText("Store", ReceiptTextStyle.toModifier().fontWeight(FontWeight.Bold))
                P(attrs = ReceiptTextStyle.toModifier().fillMaxWidth().toAttrs()) {
                    Text(store.locationId)
                }
            }

            Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.End) {
                SpanText("Date", ReceiptTextStyle.toModifier().fontWeight(FontWeight.Bold).textAlign(TextAlign.End))
                P(attrs = ReceiptTextStyle.toModifier().textAlign(TextAlign.End).toAttrs()) {
                    Text(date)
                }

                SpanText("Receipt", ReceiptTextStyle.toModifier().fontWeight(FontWeight.Bold).textAlign(TextAlign.End))
                P(attrs = ReceiptTextStyle.toModifier().textAlign(TextAlign.End).toAttrs()) {
                    Text(formatReceipt(date = date, storeId = store.id, attestationId = attestationId, receiptNumb = receiptNumb))
                }
            }
        }


        Table(
            attrs = Modifier
                .fillMaxWidth()
                .toAttrs{
                    style {
                        property("border-collapse", "collapse")
                        property("table-layout", "fixed")
                    }
                }
        )
        {
            Tr(
                attrs = Modifier.borderBottom(1.px, LineStyle.Solid, Colors.Black).toAttrs()
            ) {
                Th(ReceiptTableColumnHeader.toModifier().width(10.percent).toAttrs()) {
                    Text("Qty")
                }
                Th(ReceiptTableColumnHeader.toModifier().width(25.percent).toAttrs()) {
                    Text("SKU/Model")
                }
                Th(ReceiptTableColumnHeader.toModifier().width(25.percent).toAttrs()) {
                    Text("Description")
                }
                Th(ReceiptTableColumnHeader.toModifier().width(20.percent).toAttrs()) {
                    Text("Unit Price")
                }
                Th(ReceiptTableColumnHeader.toModifier().width(20.percent).textAlign(TextAlign.End).toAttrs()) {
                    Text("Total")
                }
            }
            receiptItems.forEach { item ->
                totalPrice += (item.unitPrice * item.quantity)

                Tr(
                    attrs = Modifier.toAttrs()
                ) {
                    Td(
                        attrs = ReceiptTableColumn.toModifier().toAttrs(),
                    ) {
                        Text(item.quantity.toString())
                    }
                    Td(
                        attrs = ReceiptTableColumn.toModifier().toAttrs(),
                    ) {
                        Text(item.skuModel)
                    }
                    Td(
                        attrs = ReceiptTableColumn.toModifier().toAttrs(),
                    ) {
                        Text(item.itemDescription)
                    }
                    Td(
                        attrs = ReceiptTableColumn.toModifier().toAttrs(),
                    ) {
                        Text(formatCurrency(item.unitPrice))
                    }
                    Td(
                        attrs = ReceiptTableColumn.toModifier().textAlign(TextAlign.End).toAttrs(),
                    ) {
                        Text(formatCurrency(totalPrice))
                    }

                }
            }
            Tr(
                attrs = Modifier.toAttrs()
            ) {
                Td {}
                Td {}
                Td {}
                Td {
                    P(attrs = ReceiptTextStyle.toModifier().fillMaxWidth().toAttrs()) {
                        SpanText("Subtotal: ", Modifier.fontWeight(FontWeight.Bold))
                    }
                }
                Td {
                    P(attrs = ReceiptTextStyle.toModifier().fillMaxWidth().textAlign(TextAlign.End).toAttrs()) {
                        Text(formatCurrency(totalPrice))
                    }
                }
            }
        }
    }
}

fun formatReceipt(date: String, storeId: Int, attestationId: Int, receiptNumb: Int): String {
    return "S${storeId}F${attestationId}-R" + receiptNumb.toString().padStart(4, '0')
}
